import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";

const H = ({ children, left }) => (
  <th className={`bg-[#2F5496] text-white text-sm font-semibold py-2 px-3 border border-slate-300 ${left ? "text-left" : "text-center"}`}>{children}</th>
);
const C = ({ children, bold, tone = "" }) => (
  <td className={`py-1.5 px-3 text-sm text-center border border-slate-200 ${bold ? "font-bold text-slate-900" : "text-slate-700"} ${tone}`}>{children}</td>
);

// Who handled the slow steps (from Invoice Flow + admin slow limits).
const DelayRanking = ({ dateFrom, dateTo }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/reports/processor-delays`, { headers: getAuthHeader(), params: { date_from: dateFrom, date_to: dateTo } })
      .then(r => setD(r.data)).catch(() => setD({ ranking: [], invoices_checked: 0 }));
  }, [dateFrom, dateTo]);
  return (
    <div className="mt-6" data-testid="delay-ranking">
      <h4 className="text-sm font-semibold text-[#2F5496] mb-1">Processor Delay Ranking</h4>
      <p className="text-[11px] text-slate-500 mb-2">People who handled steps slower than the Invoice Flow limits · {d ? `${d.invoices_checked} invoice(s) checked` : "loading…"}</p>
      {!d ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <table className="w-full border-collapse" data-testid="delay-ranking-table">
          <thead><tr><H>#</H><H left>Handled by</H><H>Slow steps</H><H>Invoices</H><H>Avg over limit</H><H left>Most often slow at</H><H left>Worst invoice</H></tr></thead>
          <tbody>
            {d.ranking.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-emerald-700 text-sm" data-testid="delay-ranking-empty">No slow steps in these dates.</td></tr>}
            {d.ranking.map(r => (
              <tr key={r.name} className={r.rank <= 3 ? "bg-rose-50/50" : ""} data-testid={`delay-row-${r.rank}`}>
                <C bold>{r.rank}</C>
                <td className="py-1.5 px-3 text-sm font-semibold text-slate-800 border border-slate-200">{r.name}</td>
                <C bold tone="text-rose-700">{r.slow_steps}</C><C>{r.invoices}</C><C>{r.avg_over}</C>
                <td className="py-1.5 px-3 text-xs border border-slate-200">{r.top_step}</td>
                <td className="py-1.5 px-3 text-xs border border-slate-200"><b>{r.worst.invoice_no}</b> · {r.worst.step} took {r.worst.took} (limit {r.worst.limit_h}h)</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Admin: per-processor output for the selected dates + pending in their services.
export const ProcessorReport = ({ dateFrom, dateTo }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setData(null); setErr("");
    axios.get(`${API}/order-register/pending-center/processors`, { headers: getAuthHeader(), params: { date_from: dateFrom, date_to: dateTo } })
      .then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || "Could not load processor report"));
  }, [dateFrom, dateTo]);
  if (err) return <div className="p-8 text-center text-rose-600" data-testid="processors-error">{err}</div>;
  if (!data) return <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const rows = data.processors || [];
  const sum = (k) => rows.reduce((n, r) => n + (r[k] || 0), 0);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/order-register/pending-center/processors/excel`, { headers: getAuthHeader(), params: { date_from: dateFrom, date_to: dateTo }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `Processors_${dateFrom}_${dateTo}.xlsx`;
      a.click();
    } catch { toast.error("Excel download failed"); }
  };
  return (
    <div className="p-4 pt-2 overflow-x-auto">
      <div className="flex justify-end mb-2">
        <button onClick={download} data-testid="processors-excel-btn" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
          <Download className="w-3.5 h-3.5" /> Download Excel
        </button>
      </div>
      <table className="w-full border-collapse" data-testid="processors-table">
        <thead>
          <tr>
            <H left>Processor</H><H left>Allotted services</H><H>Invoices created</H><H>Invoices completed</H>
            <H>Parcels processed</H><H>Rows processed</H><H>Pending parcels (services)</H><H>Slips pending</H>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No processor activity.</td></tr>}
          {rows.map(r => (
            <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`processor-row-${r.user_id}`}>
              <td className="py-1.5 px-3 text-sm border border-slate-200">
                <div className="font-semibold text-slate-800">{r.name}</div>
                <div className="text-[11px] text-slate-500">{r.email}{r.role === "admin" ? " · admin" : ""}</div>
              </td>
              <td className="py-1.5 px-3 text-xs border border-slate-200">
                <div className="flex flex-wrap gap-1">
                  {(r.services || []).length === 0 ? <span className="text-slate-400">—</span>
                    : r.services.map(s => <span key={s} className="rounded bg-slate-100 border border-slate-200 px-1.5">{s}</span>)}
                </div>
              </td>
              <C>{r.invoices_created || ""}</C>
              <C>{r.invoices_completed || ""}</C>
              <C bold>{r.parcels_processed || ""}</C>
              <C>{r.rows_processed || ""}</C>
              <C tone={r.pending_parcels ? "text-amber-700 font-semibold" : ""}>{r.pending_parcels || ""}</C>
              <C tone={r.slips_pending ? "text-rose-700 font-semibold" : ""}>{r.slips_pending || ""}</C>
            </tr>
          ))}
          <tr className="bg-[#D6E4F0]">
            <td className="py-2 px-3 text-sm font-bold border border-slate-300" colSpan={2}>Total</td>
            {["invoices_created", "invoices_completed", "parcels_processed", "rows_processed"].map(k => <C key={k} bold>{sum(k)}</C>)}
            <C bold>—</C><C bold>{sum("slips_pending")}</C>
          </tr>
        </tbody>
      </table>
      <DelayRanking dateFrom={dateFrom} dateTo={dateTo} />
      <p className="mt-2 text-[11px] text-slate-500">Pending parcels = open parcels (any reason, not on a completed invoice) within each processor's allotted services; admins see all services. Activity uses India time.</p>
    </div>
  );
};
